/**
 * listening_tracker.js — batches play/skip telemetry and ships it to the
 * server, where listening personalities are learned.
 *
 * Events are buffered in memory (mirrored to localStorage so a refresh does
 * not lose them) and flushed every 15s, when 5+ events accumulate, or on
 * pagehide via fetch keepalive.
 */

const STORAGE_KEY = 'music-pending-play-events';
const FLUSH_INTERVAL_MS = 15000;
const FLUSH_BATCH_SIZE = 5;

let buffer = [];
let flushTimer = null;
let flushing = false;
let started = false;

function loadPending() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (Array.isArray(saved) && saved.length) buffer = [...saved, ...buffer];
  } catch (_) { /* ignore */ }
}

function persistPending() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer.slice(-100)));
  } catch (_) { /* ignore */ }
}

function auth() {
  const token = localStorage.getItem('walmates-token');
  const user_id = localStorage.getItem('walmates-user_id');
  if (!token || !user_id) return null;
  return { token, user_id };
}

export async function flushPlayEvents({ keepalive = false } = {}) {
  const credentials = auth();
  if (!credentials || !buffer.length || flushing) return;

  const batch = buffer;
  buffer = [];
  persistPending();
  flushing = true;
  try {
    const res = await fetch('/api/music/events', {
      method: 'POST',
      keepalive,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${credentials.token}`,
      },
      body: JSON.stringify({ user_id: Number(credentials.user_id), events: batch }),
    });
    if (!res.ok) throw new Error(`events flush failed: ${res.status}`);
  } catch (err) {
    // Put the batch back; it will retry on the next flush.
    buffer = [...batch, ...buffer].slice(-200);
    persistPending();
    console.warn('Play event flush failed:', err?.message || err);
  } finally {
    flushing = false;
  }
}

function ensureStarted() {
  if (started) return;
  started = true;
  loadPending();
  flushTimer = setInterval(() => flushPlayEvents(), FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', () => flushPlayEvents({ keepalive: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPlayEvents({ keepalive: true });
  });
}

/**
 * evt: {video_id, source, played_ms, duration_ms, completed, skipped, persona_id}
 */
export function logPlayEvent(evt) {
  if (!evt?.video_id) return;
  ensureStarted();
  buffer.push({
    ts: Math.floor(Date.now() / 1000),
    ...evt,
  });
  persistPending();
  if (buffer.length >= FLUSH_BATCH_SIZE) flushPlayEvents();
}

export function stopTracker() {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  started = false;
}
